import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  youtubeChannel,
  youtubeLabel,
  youtubeVideoId,
  type YoutubeFrame,
  type YoutubeMessage,
  type YoutubeState,
} from '../lib/youtube';
import '../styles/studio.css';

/**
 * The player tab: one video, filling the window, and nothing else.
 *
 * This page exists to be **screen-captured by the studio**, which is why it is
 * so bare. Every pixel it paints ends up in the broadcast, so there is no
 * chrome, no padding and no background but black - and the player's own
 * controls are off, because the studio drives it over a BroadcastChannel.
 *
 * It is not part of the studio's layout in any sense. The host opens it in its
 * own tab, picks that tab in the capture dialogue, and then forgets about it.
 */

interface YoutubePlayer {
  playVideo: () => void;
  pauseVideo: () => void;
  seekTo: (seconds: number, allowSeekAhead: boolean) => void;
  loadVideoById: (videoId: string) => void;
  getCurrentTime: () => number;
  getDuration: () => number;
  getPlayerState: () => number;
  getVideoData?: () => { title?: string };
  /** Captions are a module, and turning them off means unloading it. */
  unloadModule?: (module: string) => void;
  setOption?: (module: string, option: string, value: unknown) => void;
}

interface YoutubeApi {
  Player: new (
    element: HTMLElement,
    options: {
      videoId?: string;
      playerVars?: Record<string, string | number>;
      events?: { onReady?: () => void; onStateChange?: () => void };
    },
  ) => YoutubePlayer;
}

declare global {
  interface Window {
    YT?: YoutubeApi;
    onYouTubeIframeAPIReady?: () => void;
  }
}

const PLAYING = 1;

/**
 * Turns the captions off, properly.
 *
 * `cc_load_policy` only says "do not force them on" - it does not override a
 * viewer whose account asks for automatic captions everywhere, which is why
 * they kept appearing. The module has to be unloaded, and re-unloaded after a
 * load or a play, because YouTube brings it back with the new video.
 */
function silenceCaptions(player: YoutubePlayer) {
  for (const module of ['captions', 'cc']) {
    try {
      // Clearing the track first: unloading alone leaves an already-running
      // caption on screen until the next cue boundary.
      player.setOption?.(module, 'track', {});
      player.unloadModule?.(module);
    } catch {
      // Not loaded, which is the state we wanted anyway.
    }
  }
}

/** The whole tab, when the box has not been laid out yet. */
const WHOLE: YoutubeFrame = { x: 0, y: 0, width: 1, height: 1 };

/** Loads YouTube's API once, however many times this page is mounted. */
function loadApi(): Promise<YoutubeApi> {
  return new Promise((resolve) => {
    if (window.YT?.Player) {
      resolve(window.YT);
      return;
    }
    const previous = window.onYouTubeIframeAPIReady;
    window.onYouTubeIframeAPIReady = () => {
      previous?.();
      if (window.YT) resolve(window.YT);
    };
    if (!document.querySelector('script[data-youtube-api]')) {
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';
      script.dataset.youtubeApi = 'true';
      document.head.appendChild(script);
    }
  });
}

export function YoutubeStagePage() {
  const [params] = useSearchParams();
  const initial = youtubeVideoId(params.get('v') ?? '');
  const key = params.get('k') ?? '';

  const mount = useRef<HTMLDivElement | null>(null);
  const video = useRef<HTMLDivElement | null>(null);
  const player = useRef<YoutubePlayer | null>(null);
  const videoId = useRef<string | null>(initial);
  const [problem, setProblem] = useState<string | null>(
    initial ? null : 'No video was requested. Close this tab and add the source again.',
  );

  useEffect(() => {
    if (!initial || !key) return;

    const channel = new BroadcastChannel(youtubeChannel(key));
    let cancelled = false;
    let timer = 0;

    function post(message: YoutubeMessage) {
      channel.postMessage(message);
    }

    /**
     * The 16:9 box, as fractions of the tab.
     *
     * Measured on every report rather than once: the host can resize the
     * window mid-show, and a crop taken at the old size would start showing
     * bars or cutting the picture.
     */
    function measure(): YoutubeFrame {
      const element = video.current;
      if (!element) return WHOLE;
      const rect = element.getBoundingClientRect();
      const width = window.innerWidth || 1;
      const height = window.innerHeight || 1;
      if (rect.width <= 0 || rect.height <= 0) return WHOLE;
      return {
        x: rect.left / width,
        y: rect.top / height,
        width: rect.width / width,
        height: rect.height / height,
      };
    }

    function report() {
      const active = player.current;
      if (!active) return;
      const title = active.getVideoData?.().title ?? '';
      // Named for the picker: this is the line the host reads when choosing
      // which tab to capture.
      document.title = `LiveForge - ${youtubeLabel(title, videoId.current)}`;

      const duration = active.getDuration();
      const state: YoutubeState = {
        type: 'state',
        videoId: videoId.current,
        // False while YouTube is still working out what it is playing. The
        // studio keeps its last numbers rather than showing zeroes, which is
        // what made the scrubber flick back to 0:00 between reports.
        ready: duration > 0,
        playing: active.getPlayerState() === PLAYING,
        position: active.getCurrentTime(),
        duration,
        title,
        frame: measure(),
      };
      post(state);
    }

    channel.onmessage = (event: MessageEvent<YoutubeMessage>) => {
      const message = event.data;
      const active = player.current;
      if (!active) return;

      switch (message.type) {
        case 'play':
          active.playVideo();
          silenceCaptions(active);
          break;
        case 'pause':
          active.pauseVideo();
          break;
        case 'seek':
          active.seekTo(message.seconds, true);
          break;
        case 'load':
          videoId.current = message.videoId;
          active.loadVideoById(message.videoId);
          silenceCaptions(active);
          break;
        case 'hello':
          report();
          break;
        default:
          break;
      }
    };

    void loadApi()
      .then((api) => {
        if (cancelled || !mount.current) return;
        player.current = new api.Player(mount.current, {
          videoId: initial,
          playerVars: {
            // Ours are the only controls: YouTube's would be captured too.
            controls: 0,
            disablekb: 1,
            modestbranding: 1,
            rel: 0,
            // Annotations and cards.
            iv_load_policy: 3,
            // Do not force captions on. Not sufficient by itself - see
            // silenceCaptions - because a viewer's own account setting wins.
            cc_load_policy: 0,
            // No fullscreen affordance; there is nobody here to press it.
            fs: 0,
            playsinline: 1,
          },
          events: {
            onReady: () => {
              const active = player.current;
              if (active) silenceCaptions(active);
              report();
            },
            // Every transition is a chance for captions to come back - a new
            // video, the end of an ad, a quality change - so they are cleared
            // on each one rather than only when we ask for playback.
            onStateChange: () => {
              const active = player.current;
              if (active) silenceCaptions(active);
              report();
            },
          },
        });
        // Polled rather than event-driven: the API reports state changes but
        // not position, and the studio's scrubber needs the position.
        timer = window.setInterval(report, 250);
      })
      .catch(() => {
        if (!cancelled) setProblem("YouTube's player could not be loaded.");
      });

    return () => {
      cancelled = true;
      if (timer) window.clearInterval(timer);
      channel.close();
    };
  }, [initial, key]);

  return (
    <div className="yt-stage">
      {problem ? <p className="yt-stage__problem">{problem}</p> : null}
      {/*
        Letterboxed here rather than left to fill the tab: the capture takes
        the whole window, and a 16:9 box inside it is what makes the crop the
        studio applies exact.
      */}
      {/*
        The iframe is deliberately taller than the video it plays. YouTube
        draws its title bar and its control strip against the top and bottom of
        the *player*, so giving the player letterbox bars of its own puts that
        furniture on the black - outside the 16:9 region the studio crops to.
        Hiding it was never possible from outside the iframe; keeping it out of
        frame is.
      */}
      <div className="yt-stage__box">
        <div className="yt-stage__player" ref={mount} />
        {/* Not painted. It exists to be measured. */}
        <div className="yt-stage__video" ref={video} />
      </div>
    </div>
  );
}
